import type { BoxProps, MotionPropsFor } from '@hover/blueprint';
import {
  Box,
  Button,
  IconButton,
  Image,
  SimpleGrid,
  motion,
  useTheme,
} from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import { iHeart, iHome, iPlusSquare, iUser } from '@hover/icons';
import blackHoverIcon from '@hover/tokens-brand/assets/logo-mark--black.svg';
import { useMotionTemplate } from 'framer-motion';
import React from 'react';
import { useHistory } from 'react-router';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route } from 'src/shared/route';

import {
  HomeownerAppMenuNames,
  useHomeownerAppContext,
  useMaxWidthQuery,
} from '../../hooks';

import { ToolbarButton } from './ToolbarButton';
import { desktopToolbarHeight, mobileToolbarHeight } from './constants';
import { useToolbarScrollValue } from './useToolbarScroll';

type ToolbarNameType = 'home' | 'upload' | 'favorites';

export type ToolbarItemType = {
  name: ToolbarNameType;
  icon: IconProp;
  path?: string;
  mobileOrder: number;
  desktopOrder: number;
};

export interface ToolbarProps extends MotionPropsFor<BoxProps> {}

const MotionBox = motion(Box);

const toolbarItems: ToolbarItemType[] = [
  {
    name: 'home',
    icon: iHome,
    mobileOrder: 1,
    desktopOrder: 1,
    path: Route.HOMEOWNER_APP_HOME.path,
  },
  {
    name: 'upload',
    mobileOrder: 2,
    desktopOrder: 3,
    icon: iPlusSquare,
  },
  {
    name: 'favorites',
    icon: iHeart,
    mobileOrder: 3,
    desktopOrder: 2,
    path: Route.HOMEOWNER_APP_FAVORITES.path,
  },
];

const Toolbar = ({ ...props }: ToolbarProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { loggedIn, setCurrentMenu } = useHomeownerAppContext();

  const belowTablet = useMaxWidthQuery('tablet');
  const { colors, zIndices } = useTheme();

  const opacityMotion = useToolbarScrollValue(0, 0.7);
  const blurMotion = useToolbarScrollValue(0, 10);

  const backgroundColor = useMotionTemplate`rgba(255, 255, 255, ${opacityMotion})`;
  const backdropFilter = useMotionTemplate`blur(${blurMotion}px)`;

  const history = useHistory();

  const handleClick = (item: ToolbarItemType) => {
    switch (item.name) {
      case 'upload':
        setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
        break;
      default:
        history.push(item.path!);
    }
  };

  return (
    <MotionBox
      alignItems="center"
      as="nav"
      bottom={{ base: 0, tablet: undefined }}
      height={{
        base: `${mobileToolbarHeight}px`,
        tablet: `${desktopToolbarHeight}px`,
      }}
      justifyContent="space-between"
      minW="100%"
      paddingX={{ base: 0, tablet: 700 }}
      paddingY={{ base: 0, tablet: 600 }}
      position={{ base: 'fixed', tablet: 'sticky' }}
      style={{ backdropFilter, backgroundColor }}
      sx={belowTablet({
        background: `${colors.alpha(0.075, 'neutral.200')} !important`,
        backdropFilter: 'blur(10px) !important',
      })}
      top={{ base: undefined, tablet: 0 }}
      zIndex={zIndices.overlay - 1}
      {...props}
    >
      <Box
        display={{ base: 'none', tablet: 'flex' }}
        justifyContent="center"
        width={600}
      >
        <Image alt="hover logo" src={blackHoverIcon} width={350} />
      </Box>
      <SimpleGrid
        columns={toolbarItems.length}
        height={{
          base: `${mobileToolbarHeight}px`,
          tablet: `${desktopToolbarHeight}px`,
        }}
        paddingInline={900}
        width={{ base: '100%', tablet: 'auto' }}
      >
        {toolbarItems.map(item => (
          <ToolbarButton
            key={item.name}
            onClick={() => handleClick(item)}
            toolbarItem={item}
          />
        ))}
      </SimpleGrid>
      <Box
        display={{ base: 'none', tablet: 'flex' }}
        justifyContent="center"
        width={600}
      >
        {loggedIn ? (
          <IconButton icon={iUser} label="account" size="large" />
        ) : (
          <Button variant="ghost">{t('home.logIn')}</Button>
        )}
      </Box>
    </MotionBox>
  );
};

export { Toolbar };
